import { snakeCase } from "lodash";

import type { ClickElementDetails, ClickElementInput } from "../pipeline";

export const PREMADE_LISTING_PAGE_CATEGORY = "premade_listing";
export const PREMADE_SHEET_PAGE_CATEGORY = "premade_sheet";

/**
 * The premade that was clicked, sent as ELEMENT_ID and ITEM_NAME
 */
export interface ClickedPremade {
  characterId: number;
  name: string;
}

const getPremadeClick = (
  elementName: string,
  elementType: "button" | "card",
  pageCategory: string,
  premade: ClickedPremade,
  details: ClickElementDetails = {}
): ClickElementInput => ({
  elementName,
  elementType,
  elementId: String(premade.characterId),
  pageCategory,
  details: {
    itemName: premade.name,
    actionDetail: `${elementName}_${premade.characterId}`,
    ...details,
  },
});

/**
 * The Choose button on a premade listing card
 */
export const getChoosePremadeClick = (
  premade: ClickedPremade
): ClickElementInput =>
  getPremadeClick(
    "choose_premade",
    "button",
    PREMADE_LISTING_PAGE_CATEGORY,
    premade
  );

/**
 * Previewing a premade's sheet from the listing, either with the preview
 * (eye) button or by clicking the card itself
 */
export const getPreviewPremadeClick = (
  premade: ClickedPremade,
  elementType: "button" | "card",
  targetUrl: string
): ClickElementInput =>
  getPremadeClick(
    "preview_premade",
    elementType,
    PREMADE_LISTING_PAGE_CATEGORY,
    premade,
    {
      targetUrl,
    }
  );

/**
 * An option selected or deselected in a premade listing filter dropdown, for
 * example "select_wizard" on "class_filter"
 */
export const getPremadeFilterClick = (
  filterKey: string,
  option: { label: string; value: string | number | null },
  isSelected: boolean
): ClickElementInput => {
  const itemName = `${filterKey}_filter`;
  return {
    elementName: itemName,
    elementType: "dropdown",
    pageCategory: PREMADE_LISTING_PAGE_CATEGORY,
    details: {
      itemName,
      actionDetail: `${isSelected ? "select" : "deselect"}_${snakeCase(
        option.label
      )}`,
    },
  };
};

/**
 * A Rule Set tab chosen on the premade listing, for example "set_5e" or
 * "set_all"
 */
export const getPremadeRulesetClick = (label: string): ClickElementInput => ({
  elementName: "ruleset_filter",
  elementType: "tab_filter",
  pageCategory: PREMADE_LISTING_PAGE_CATEGORY,
  details: {
    itemName: "ruleset_filter",
    actionDetail: `set_${snakeCase(label)}`,
  },
});

/**
 * The "Back to Campaign" / "Back to Join Campaign" button on the premade
 * listing
 */
export const getBackToCampaignClick = (
  campaignJoinCode: string | null,
  targetUrl: string
): ClickElementInput => ({
  elementName: "back_to_campaign",
  elementType: "button",
  pageCategory: PREMADE_LISTING_PAGE_CATEGORY,
  details: {
    itemName: "back_to_campaign",
    actionDetail: campaignJoinCode
      ? "back_to_join_campaign"
      : "back_to_campaign",
    targetUrl,
  },
});

/**
 * The "Subscribe" link in the premade listing's alert shown when the user's
 * character slots are full
 */
export const getPremadeSubscribeClick = (
  targetUrl: string
): ClickElementInput => ({
  elementName: "subscribe_link",
  elementType: "link",
  pageCategory: PREMADE_LISTING_PAGE_CATEGORY,
  details: {
    itemName: "subscribe_link",
    actionDetail: "character_slots_full_alert",
    targetUrl,
  },
});

/**
 * The "Watch Tour" button on a premade's sheet
 */
export const getWatchTourClick = (premade: ClickedPremade): ClickElementInput =>
  getPremadeClick(
    "watch_tour_video",
    "button",
    PREMADE_SHEET_PAGE_CATEGORY,
    premade
  );

/**
 * Claiming a premade from its sheet, either Claim button
 * or from within the tour dialog
 */
export const getClaimPremadeClick = (
  premade: ClickedPremade,
  from: "sheet_button" | "tour_dialog"
): ClickElementInput =>
  getPremadeClick(
    "claim_premade",
    "button",
    PREMADE_SHEET_PAGE_CATEGORY,
    premade,
    {
      itemFormat: from,
    }
  );
